// 캐릭터 시스템 공용 약속(docs/CHARACTER-PRD.md §1·§4·§5·§13): 클라이언트(src/characters)와 Worker(src/server/characters.ts)가 같이 쓴다.
// 순수 상수·타입만 둔다(DOM·저장소·게임 모듈 import 없음 — Worker 번들을 가볍게).

/** 직업 = 그 직업의 얼굴 영웅 id(검사 kael · 궁수 lyra · 마법사 seren · 수호자 brann · 사제 lumen) */
export type ClassId = 'kael' | 'lyra' | 'seren' | 'brann' | 'lumen';
export const CLASS_IDS: readonly ClassId[] = ['kael', 'lyra', 'seren', 'brann', 'lumen'];
/** 직업 이름(캐릭터 생성 화면·프로필) */
export const CLASS_LABEL: Readonly<Record<ClassId, string>> = { kael: '검사', lyra: '궁수', seren: '마법사', brann: '수호자', lumen: '사제' };
/** 외형 고정 + 색 변형 수(0..LOOK_COUNT-1) */
export const LOOK_COUNT = 3;

/** 캐릭터 슬롯: 기본 3칸, 확장 자리 최대 8칸(유료 연결 없음 — 확장은 별도 승인) */
export const SLOT_BASE = 3;
export const SLOT_MAX = 8;
/** 지금은 월드 1개. 이름 유일 범위가 월드 단위라 스키마에 미리 둔다 */
export const WORLD_ID = 1;

/** 캐릭터 id: 'c_' + 24 hex(계정 id 'a_' + 24 hex와 같은 꼴). 클라이언트가 만들어 서버에 제안하고, 같은 id 재요청은 멱등이다 */
export const CHARACTER_ID_RE = /^c_[0-9a-f]{24}$/;
export function isCharacterId(v: unknown): v is string { return typeof v === 'string' && CHARACTER_ID_RE.test(v); }
export function isClassId(v: unknown): v is ClassId { return typeof v === 'string' && (CLASS_IDS as readonly string[]).includes(v); }

/** 태그 범위(4자리 숫자 0001~9999) */
export const TAG_MIN = 1;
export const TAG_MAX = 9999;
/** 레벨 이 이상 캐릭터는 삭제 뒤 유예(그동안 복구 가능, 이름 예약 유지) */
export const DELETE_GRACE_LEVEL = 30;
export const DELETE_GRACE_MS = 7 * 86_400_000;

/** 이름 검사 실패 사유(UI 문구는 nameReasonText) */
export type NameReason = 'empty' | 'length' | 'charset' | 'reserved' | 'banned';

/** 서버가 돌려주는 캐릭터 한 줄 */
export interface CharacterView {
  id: string;
  worldId: number;
  slot: number;
  cls: ClassId;
  look: number;
  /** 표시 이름(NFC). 이관 캐릭터는 첫 접속에 이름을 정하기 전까지 null */
  name: string | null;
  /** 4자리 태그(이름이 없으면 null) */
  tag: number | null;
  createdAt: number;
  /** 무료 이름 변경이 남았는가(처음 1회) */
  renameFree: boolean;
  /** 삭제 대기(유예 중)면 삭제 시각과 영구 삭제 시각 */
  deletedAt: number | null;
  purgeAt: number | null;
  /** 이관으로 만든 캐릭터인가(origin = 'legacy-kael' | 'legacy-lyra' | 'retained') */
  origin: string | null;
  /** 서버 캐릭터 스냅샷 rev(없으면 0) — 로컬 설치 rev와 비교해 「받는 중」을 정한다(index.ts deriveRosterState) */
  rev: number;
}
export interface SlotView { base: number; cap: number; max: number }
export interface CharacterListResponse { worldId: number; characters: CharacterView[]; slots: SlotView }
export interface CharacterResponse { character: CharacterView }
/** /api/characters/migration*: 계정 이관 상태. mine = 이 claim이 진행 중 이관을 소유(임대 안) · done = 완료(서버 캐릭터가 정본) */
export interface MigrationResponse { allocation: unknown | null; hash: string | null; done: boolean; leaseUntil: number; mine: boolean; /** 소유자에게만: 펜싱 토큰 */ epoch?: number }

/** POST /api/characters 본문. id가 멱등 키다(같은 계정 + 같은 id = 같은 결과) */
export interface CreateCharacterRequest {
  id: string;
  cls: ClassId;
  look: number;
  /** 이름(이관 캐릭터는 null로 자리만 만들 수 있다 — 이름은 PATCH로) */
  name: string | null;
  /** 이관 캐릭터 표시(서버 기록용) */
  origin?: string | null;
  /** 이관 캐릭터: 진행 중 이관을 소유한 claim과 펜싱 epoch(서버가 같은 문장에서 확인한다 — 넘겨받힌 기기의 늦은 등록 거절) */
  claim?: string;
  epoch?: number;
}
/** PATCH /api/characters/:id 본문(이름 정하기·바꾸기) */
export interface RenameCharacterRequest { name: string }

/** 캐릭터 API 오류 코드(본문 {error}) — account/protocol ErrorCode에 합쳐진다 */
export type CharacterErrorCode =
  | 'bad-name' | 'name-full' | 'slots-full' | 'id-taken' | 'rename-locked' | 'character-deleted' | 'not-restorable';

/** `이름#태그` 문자열(태그 4자리 0 채움) */
export function formatHandle(name: string, tag: number): string { return name + '#' + String(tag).padStart(4, '0'); }
